import type { LoginPrompt } from '@iheartjane/data-access';
import {
  Banner,
  Card,
  Flex,
  Link,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import type { Nullish } from '@iheartjane/types';
import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { hex } from 'wcag-contrast';

import { ColorPicker } from '@jane/business-admin/components/premium/ColorPicker';
import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import type { BloomOperatorConfig } from '@jane/business-admin/types';
import { businessPaths } from '@jane/business-admin/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';
import { LabeledTextField } from './components/signInPrompt/LabeledTextField';
import type { TextFieldName } from './components/signInPrompt/types';

const WCAG_RATIO = 4.5;

const CONTRAST_WARNING_MESSAGE =
  'Button and button text contrast is below 4.5:1 (WCAG AA). Consider choosing colors with higher contrast.';
const HEX_ERROR_MESSAGE = 'Enter a valid 6-digit hex color.';

const LIMITS = {
  header: 30,
  desktop_line: 30,
  mobile_header: 30,
  mobile_copy: 120,
  cta_label: 20,
};

type ColorFieldName = 'colors.button' | 'colors.button_text';

const isFilledHexColor = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);
const isHexColor = (value: Nullish<string>) =>
  !value || isFilledHexColor(value);

// Menu primary may be 3-digit hex ("#fff"); expand to 6-digit so it passes
// this page's 6-digit-only validation instead of blocking Publish/Preview.
const toSixDigitHex = (value: Nullish<string>, fallback: string): string => {
  if (!value) return fallback;
  const short = value.match(/^#([0-9a-fA-F]{3})$/);
  if (short) {
    return `#${short[1]
      .split('')
      .map((c) => c + c)
      .join('')}`;
  }
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
};

// Blank → null (the canonical "inherit" value); leave malformed non-empty
// values (e.g. "#12") intact so validation can surface them.
const normalizeNullableColor = (value: Nullish<string>): string | null =>
  value && value.trim() !== '' ? value : null;

// Keep unset colors as null so they keep inheriting the menu primary;
// materializing the inherited hex here would freeze colors on any publish.
const buildFormValues = (
  defaults: LoginPrompt,
  loginPrompt: Nullish<LoginPrompt>
): NonNullable<LoginPrompt> => ({
  ...defaults,
  ...loginPrompt,
  colors: {
    button: normalizeNullableColor(loginPrompt?.colors?.button),
    button_text: normalizeNullableColor(loginPrompt?.colors?.button_text),
  },
});

const SectionCard = ({
  id,
  title,
  subtitle,
  children,
}: {
  children: ReactNode;
  id: string;
  subtitle?: string;
  title: string;
}) => (
  <Card border="grays-ultralight" id={id}>
    <Card.Content p={40}>
      <Flex flexDirection="column" gap={16}>
        <Flex flexDirection="column">
          <Typography variant="header-bold" branded>
            {title}
          </Typography>
          {subtitle && <Typography color="grays-mid">{subtitle}</Typography>}
        </Flex>
        {children}
      </Flex>
    </Card.Content>
  </Card>
);

export const PremiumSignInPrompt = () => {
  const bloomMenuQuery = useFetchCurrentBloomMenu();

  if (!bloomMenuQuery.data) return null;

  return <InnerPremiumSignInPrompt bloomMenu={bloomMenuQuery.data} />;
};

const InnerPremiumSignInPrompt = ({
  bloomMenu,
}: {
  bloomMenu: BloomOperatorConfig;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [previewLoading, setPreviewLoading] = useState(false);

  const { mutate: updateBloomMenu, isPending } = useUpdateBloomMenu(
    bloomMenu.id
  );

  const bloomTheme = bloomMenu.theme?.bloom;
  const defaults: LoginPrompt = bloomMenu.default_theme.bloom!.login_prompt!;

  // Pre-fill pickers from the menu primary; fallbacks mirror Bloom's defaults.
  const inheritedButtonColor = toSixDigitHex(
    bloomTheme?.colors?.primary?.main,
    '#0E0E0E'
  );
  const inheritedButtonTextColor = toSixDigitHex(
    bloomTheme?.colors?.primary?.contrast,
    '#FFFFFF'
  );

  const formMethods = useForm<LoginPrompt>({
    mode: 'onChange',
    defaultValues: buildFormValues(defaults, bloomTheme?.login_prompt),
  });

  const { control, setValue, getValues, reset } = formMethods;

  const publish: SubmitHandler<FieldValues> = useCallback(
    (data) => {
      updateBloomMenu(
        {
          theme: {
            ...bloomTheme,
            login_prompt: data,
          },
        },
        {
          onError: (error) => {
            console.error('[PremiumSignInPrompt] publish failed:', error);
            toast.add({ label: 'An error occurred.', variant: 'error' });
          },
          onSuccess: () => {
            toast.add({ label: 'Menu updated!', variant: 'success' });
            reset(data);
            navigate(businessPaths.premium());
          },
        }
      );
    },
    [updateBloomMenu, bloomTheme, toast, reset, navigate]
  );

  const preview = useCallback(() => {
    setPreviewLoading(true);
    const data = getValues();
    updateBloomMenu(
      {
        theme_draft: {
          ...bloomTheme,
          login_prompt: data,
        },
      },
      {
        onError: (error) => {
          console.error('[PremiumSignInPrompt] preview failed:', error);
          setPreviewLoading(false);
          toast.add({ label: 'An error occurred.', variant: 'error' });
        },
        onSuccess: () => {
          setPreviewLoading(false);
          const menuUrl = bloomMenu.menu_url;
          if (!menuUrl) return;
          const previewUrl = new URL(menuUrl);
          previewUrl.searchParams.set('draft', 'true');
          previewUrl.searchParams.set('sign-in-prompt', 'true');
          const newWindow = window.open(
            previewUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (newWindow) newWindow.opener = null;
        },
      }
    );
  }, [getValues, updateBloomMenu, bloomTheme, bloomMenu.menu_url, toast]);

  // Watched values drive the live contrast banner; the validator below
  // re-checks the same threshold to disable Publish.
  const buttonColor = useWatch({ control, name: 'colors.button' });
  const buttonTextColor = useWatch({
    control,
    name: 'colors.button_text',
  });
  const effectiveButtonColor = buttonColor || inheritedButtonColor;
  const effectiveButtonTextColor = buttonTextColor || inheritedButtonTextColor;
  const contrast =
    isFilledHexColor(effectiveButtonColor) &&
    isFilledHexColor(effectiveButtonTextColor)
      ? hex(effectiveButtonColor, effectiveButtonTextColor)
      : null;
  const showContrastWarning = contrast !== null && contrast < WCAG_RATIO;
  const validateHexColor = (value: string | null | undefined) =>
    isHexColor(value) || HEX_ERROR_MESSAGE;
  const validateContrast = (
    _v: string | null | undefined,
    values: LoginPrompt
  ) => {
    const bg = values.colors?.button || inheritedButtonColor;
    const fg = values.colors?.button_text || inheritedButtonTextColor;
    if (!isFilledHexColor(bg) || !isFilledHexColor(fg)) return true;
    return hex(bg, fg) >= WCAG_RATIO || CONTRAST_WARNING_MESSAGE;
  };

  const revertText = (field: TextFieldName) => {
    setValue(field, defaults[field], { shouldDirty: true });
  };

  // null (not the inherited hex) so the color resumes inheriting.
  const revertColor = (field: ColorFieldName) => {
    setValue(field, null, { shouldDirty: true, shouldValidate: true });
  };

  const colorFields: {
    current: string | null | undefined;
    dep: ColorFieldName;
    inherited: string;
    label: string;
    name: ColorFieldName;
  }[] = [
    {
      name: 'colors.button_text',
      label: 'Button text',
      dep: 'colors.button',
      inherited: inheritedButtonTextColor,
      current: buttonTextColor,
    },
    {
      name: 'colors.button',
      label: 'Primary button',
      dep: 'colors.button_text',
      inherited: inheritedButtonColor,
      current: buttonColor,
    },
  ];

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isPending && !previewLoading}
      onSubmit={publish}
      preview={{
        onClick: preview,
        disable: isPending,
        loading: isPending && previewLoading,
      }}
      title="Sign-in prompt"
    >
      <Flex
        flexDirection="column"
        p={32}
        mt={8}
        gap={24}
        maxWidth={1008}
        mx="auto"
      >
        <SectionCard id="desktop" title="Desktop">
          <LabeledTextField
            defaultValue={defaults['header']}
            name="header"
            label="Header"
            maxLength={LIMITS.header}
            onRevert={() => revertText('header')}
          />
          <LabeledTextField
            defaultValue={defaults['desktop_line_1']}
            name="desktop_line_1"
            label="Line 1"
            maxLength={LIMITS.desktop_line}
            onRevert={() => revertText('desktop_line_1')}
          />
          <LabeledTextField
            defaultValue={defaults['desktop_line_2']}
            name="desktop_line_2"
            label="Line 2"
            maxLength={LIMITS.desktop_line}
            onRevert={() => revertText('desktop_line_2')}
          />
          <LabeledTextField
            defaultValue={defaults['desktop_line_3']}
            name="desktop_line_3"
            label="Line 3"
            maxLength={LIMITS.desktop_line}
            onRevert={() => revertText('desktop_line_3')}
          />
        </SectionCard>

        <SectionCard id="mobile" title="Mobile">
          <LabeledTextField
            defaultValue={defaults['mobile_header']}
            name="mobile_header"
            label="Header"
            maxLength={LIMITS.mobile_header}
            onRevert={() => revertText('mobile_header')}
          />
          <LabeledTextField
            defaultValue={defaults['mobile_copy']}
            name="mobile_copy"
            label="Line 1"
            maxLength={LIMITS.mobile_copy}
            multiline
            onRevert={() => revertText('mobile_copy')}
          />
        </SectionCard>

        <SectionCard
          id="cta"
          title="Call-to-action"
          subtitle="Applies to both Desktop and Mobile experiences."
        >
          <LabeledTextField
            defaultValue={defaults['cta_label']}
            name="cta_label"
            label="Copy"
            maxLength={LIMITS.cta_label}
            onRevert={() => revertText('cta_label')}
          />
        </SectionCard>

        <SectionCard
          id="colors"
          title="Colors"
          subtitle="Your menu's colors are used by default, edit these here if you want your login prompt colors to be different."
        >
          {showContrastWarning && (
            <Banner variant="error" full label={CONTRAST_WARNING_MESSAGE} />
          )}
          <Flex gap={24}>
            {colorFields.map(({ name, label, dep, inherited, current }) => (
              <Flex key={name} flexDirection="column" gap={8} width="100%">
                <Controller
                  control={control}
                  name={name}
                  rules={{
                    validate: {
                      hex: validateHexColor,
                      a11y: validateContrast,
                    },
                    deps: [dep],
                  }}
                  render={({ field }) => (
                    <ColorPicker
                      label={label}
                      labelAlign="left"
                      labelColor="grays-black"
                      value={field.value || inherited}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      shouldStretch
                      mr={0}
                    />
                  )}
                />
                <Flex justifyContent="flex-end">
                  <Link
                    onClick={() => revertColor(name)}
                    color={current ? 'grays-black' : 'grays-mid'}
                    typography="body"
                  >
                    Revert to default
                  </Link>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </SectionCard>
      </Flex>
    </ModalPageWrapper>
  );
};
